import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { REACT_APP_GLOBAL_API_BASE_URL_V2 } from 'runtimeEnvVars';

import {
  fetchApplicantDetailsInit,
  setApplicantDetail,
  updateApplicantTableStatuses,
} from 'containers/ApplicantTableV2/actions';
import { addErrorMessageFromCaughtValueI18n } from 'containers/FlashMessage/actions';
import { apiGet } from 'utils/axios';

export const fetchApplicantData = applicantId => {
  const url = `${REACT_APP_GLOBAL_API_BASE_URL_V2}/applicants/${applicantId}`;
  return apiGet(url);
};

export default function useApplicantDetail(
  applicantId,
  defaultFetching = true,
  { onSettled, reportUnavailable = false } = {},
) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [data, setData] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState('');
  const [settlement, setSettlement] = useState(null);
  const requestIdRef = useRef(0);
  const loadedApplicantIdRef = useRef(null);
  const currentApplicantIdRef = useRef(applicantId);
  currentApplicantIdRef.current = applicantId;
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const reportUnavailableRef = useRef(reportUnavailable);
  reportUnavailableRef.current = reportUnavailable;

  const fetchData = useCallback(() => {
    const reportSettled = onSettledRef.current;
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const isCurrent = () =>
      requestId === requestIdRef.current &&
      currentApplicantIdRef.current === applicantId;
    if (reportUnavailableRef.current) {
      setError('');
      setSettlement(null);
      if (loadedApplicantIdRef.current !== applicantId) {
        setData(null);
        setIsFetching(true);
      }
    }
    let outcome = 'failed';
    dispatch(fetchApplicantDetailsInit({ applicantId }));
    dispatch(
      setApplicantDetail({ info: { id: applicantId }, isFetching: true }),
    );
    fetchApplicantData(applicantId)
      .then(res => {
        if (!isCurrent()) return;
        const { applicant } = res.data;

        setData(applicant);
        loadedApplicantIdRef.current = applicant ? applicantId : null;
        dispatch(updateApplicantTableStatuses(applicantId, applicant.status));
        setError('');
        if (applicant) outcome = 'ok';
      })
      .catch(err => {
        if (!isCurrent()) return;
        if (err?.status === 403 || err?.status === 404) outcome = 'unavailable';
        if (
          reportUnavailableRef.current &&
          loadedApplicantIdRef.current !== applicantId
        ) {
          loadedApplicantIdRef.current = null;
          setData(null);
        }
        setError(
          reportUnavailableRef.current && outcome === 'unavailable'
            ? ''
            : err?.message || err,
        );
      })
      .finally(() => {
        if (!isCurrent()) return;
        setSettlement({ applicantId, outcome });
        setIsFetching(false);
        reportSettled?.(outcome);
      });
  }, [applicantId, dispatch]);

  // The interceptor rejects with the response body's message, so a 403 names
  // the denial instead of the generic error.
  useEffect(() => {
    if (error) {
      dispatch(addErrorMessageFromCaughtValueI18n(error, intl));
    }
  }, [dispatch, error, intl]);

  useEffect(() => {
    if (applicantId && defaultFetching) {
      fetchData();
    }
    return () => {
      requestIdRef.current += 1;
    };
  }, [applicantId, fetchData, defaultFetching]);

  const currentOutcome =
    reportUnavailable &&
    loadedApplicantIdRef.current !== applicantId &&
    settlement?.applicantId === applicantId
      ? settlement.outcome
      : null;
  return {
    data,
    error,
    isFetching,
    fetchData,
    setIsFetching,
    unavailable: currentOutcome === 'unavailable',
    failed: currentOutcome === 'failed',
  };
}
